import { Image, Flex, Text, Spinner } from "@chakra-ui/react";
import {
  AuthFormContainer,
  AuthCardEllipse,
  AuthCardTopFrame,
  AuthHeading,
  AuthSubheading,
  AuthButton,
  LinkText,
} from "@root/commonStyles/commonStyle";
import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getPasswordRecoverEmail } from "@root/store/selectors/entities";
import {
  resendEmail,
  resetPass,
  verifyResetPasswordOtpThunk,
} from "@root/store/thunks";
import { VerificationInput } from "./styles";
import { useOrganizationBranding } from "@root/context/organization-branding-context";
import { useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { ROUTE_NAMES } from "@root/constants";
import { useGlobalContext } from "@root/context/global-context";

export const EmailVerification: React.FC = () => {
  const {
    logoUrl,
    defaultLogoUrl,
    loading: brandingLoading,
  } = useOrganizationBranding();
  const givenEmail: any = useSelector(getPasswordRecoverEmail);
  const { verifyOtpLoading, verifyOtp, resendOtp } = useGlobalContext();
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const dispatch = useDispatch();
  const onSuccess = () => {
    setLoading(false);
  };

  const isResetting: boolean = givenEmail?.redirect_url;
  const onResend = () => {
    setLoading(true);
    if (isResetting) {
      dispatch(
        resetPass({
          payload: givenEmail,
          onSuccess,
          onError: () => setLoading(false),
        })
      );
    } else {
      dispatch(
        resendEmail({
          payload: givenEmail,
          onSuccess,
          onError: () => setLoading(false),
        })
      );
    }
  };

  const { t } = useTranslation("common");

  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleOtpChange = (value: string, idx: number) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[idx] = value;
    setOtp(newOtp);
    setError("");
    if (value && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
    if (!value && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowRight" && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setError("");
    setSuccess(false);
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setError(t("emailVerification.otpLengthError"));
      setLoading(false);
      return;
    }
    setLoading(true);
    const action: any = await dispatch(
      verifyResetPasswordOtpThunk({
        email: givenEmail?.email,
        otp: otpCode,
        onSuccess: () => setSuccess(true),
        onError: (msg: string) => setError(msg),
      })
    );
    setLoading(false);
    if (action?.payload?.data?.resetToken && action?.payload?.data?.email) {
      navigate(ROUTE_NAMES.RESET_PASSWORD, {
        state: {
          resetToken: action.payload.data.resetToken,
          email: action.payload.data.email,
        },
      });
    }
  };
  const { email } = givenEmail;

  const handleResendOtp = async () => {
    console.log("Resend clicked");
    await resendOtp({
      payload: {
        email,
      },
      onSuccess: () => {
        setOtp(["", "", "", "", "", ""]);
        setHasError(false);
      },
      onError: () => {
        setHasError(true);
      },
    });
  };

  const renderVerificationInputs = (
    inputsRef: React.MutableRefObject<(HTMLInputElement | null)[]>
  ) => {
    return Array.from({ length: 6 }).map((_, idx) => (
      <React.Fragment key={idx}>
        <VerificationInput
          ref={(el: HTMLInputElement | null) => (inputsRef.current[idx] = el)}
          maxLength={1}
          type="text"
          autoComplete="off"
          inputMode="numeric"
          placeholder="0"
          value={otp[idx]}
          onChange={(e) => handleOtpChange(e.target.value, idx)}
          onKeyDown={(e) => handleOtpKeyDown(e, idx)}
        />
        {idx === 2 && (
          <Text
            fontSize="32px"
            fontWeight={600}
            color="#C7C7C7"
            mx={2}
            key="dash"
          >
            -
          </Text>
        )}
      </React.Fragment>
    ));
  };

  return (
    <AuthFormContainer maxW="632px">
      <AuthCardEllipse />
      <AuthCardTopFrame style={{ position: "relative", overflow: "hidden" }}>
        <Flex align="center" gap="12.8px" mb={2} minH="38px">
          {brandingLoading ? (
            <Spinner size="md" />
          ) : (
            <Image
              src={logoUrl || defaultLogoUrl}
              alt="logo"
              maxH="38px"
              objectFit="contain"
            />
          )}
        </Flex>
        <AuthHeading mt={2}>{t("emailVerification.subTitle")}</AuthHeading>
        <AuthSubheading>
          {t("emailVerification.subtitle_1")}
          <br />
          {t("emailVerification.subtitle_2")}
        </AuthSubheading>
      </AuthCardTopFrame>

      <Flex justify="center" align="center" gap={2}>
        {renderVerificationInputs(inputsRef)}
      </Flex>
      {error && (
        <Text color="red.500" mt={2} textAlign="center">
          {error}
        </Text>
      )}
      {success && (
        <Text color="green.500" mt={2} textAlign="center">
          {t("emailVerification.otpSuccess") || "OTP Verified!"}
        </Text>
      )}
      <Flex
        direction="column"
        align="center"
        justify="center"
        width="100%"
        mb={3}
      >
        <Flex align="center" justify="center">
          <AuthSubheading>{t("emailVerification.codeNotGet")}</AuthSubheading>
          <LinkText
            to="#"
            color="purple.500"
            fontSize="15px"
            ml={1}
            onClick={handleResendOtp}
          >
            {t("emailVerification.resendLink")}
          </LinkText>
        </Flex>
      </Flex>
      <AuthButton
        width="100%"
        fontSize="20px"
        height="56px"
        onClick={handleVerify}
        isLoading={loading}
      >
        {t("emailVerification.verifyEmail")}
      </AuthButton>
    </AuthFormContainer>
  );
};
